import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { fetcherAuth } from "utils/api";

const MAX_BRANDS_LIMIT = 400;

interface Props {
  open: boolean;
  handleClose: () => void;
}

enum WeekSelection {
  this_week = "this_week",
  next_week = "next_week",
  none = "none",
}

export default function RunPitchScheduleDialog({ open, handleClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [numberOfBrands, setNumberOfBrands] = useState<number>(0);
  const [numberOfRepitchBrands, setNumberOfRepitchBrands] = useState<number>(0);

  const [weekSelection, setWeekSelection] = useState<WeekSelection>(
    WeekSelection.none,
  );

  const [overrideStartDate, setOverrideStartDate] = useState<string | null>(
    null,
  );
  const [overrideEndDate, setOverrideEndDate] = useState<string | null>(null);

  const [validation, setValidation] = useState<string>("");
  const [validationMessage, setValidationMessage] = useState<string>("");

  const hasOverrideDates = Boolean(overrideStartDate && overrideEndDate);

  const { getToken } = useAuth();
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  useEffect(() => {
    if (hasOverrideDates) {
      setWeekSelection(WeekSelection.none);
    }
  }, [hasOverrideDates]);

  const validateFields = () => {
    if (numberOfBrands < 0 || numberOfBrands > MAX_BRANDS_LIMIT) {
      setValidation("numberOfBrands");
      setValidationMessage(
        `The number must be between 0 and ${MAX_BRANDS_LIMIT}.`,
      );
      return false;
    }

    if (numberOfRepitchBrands < 0 || numberOfRepitchBrands > MAX_BRANDS_LIMIT) {
      setValidation("numberOfRepitchBrands");
      setValidationMessage(
        `The number must be between 0 and ${MAX_BRANDS_LIMIT}.`,
      );
      return false;
    }

    if (numberOfBrands === 0 && numberOfRepitchBrands === 0) {
      setValidation("general");
      setValidationMessage(
        "You must select at least one brand to email or re-pitch.",
      );
      return false;
    }

    if (!hasOverrideDates && weekSelection === WeekSelection.none) {
      setValidation("general");
      setValidationMessage(
        "Please select a week or provide a custom date range.",
      );
      return false;
    }

    if (overrideStartDate && overrideEndDate) {
      if (new Date(overrideStartDate) > new Date(overrideEndDate)) {
        setValidation("general");
        setValidationMessage("Start date must be before end date.");
        return false;
      }
    }

    setValidation("");
    setValidationMessage("");
    return true;
  };

  const handleRun = async () => {
    if (!validateFields()) return;

    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bulk-send-settings/run`,
        "POST",
        {},
        {
          numberOfBrands,
          numberOfRepitchBrands,
          weekSelection:
            weekSelection === WeekSelection.none || hasOverrideDates
              ? null
              : weekSelection,
          overrideStartDate: overrideStartDate ?? null,
          overrideEndDate: overrideEndDate ?? null,
        },
      );

      setAlert("Successfully started pitch scheduling", "success");
      handleClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: number) => {
    if (field === "numberOfBrands") {
      setNumberOfBrands(value);
    } else {
      setNumberOfRepitchBrands(value);
    }

    if (value >= 0 && value <= MAX_BRANDS_LIMIT) {
      setValidation("");
      setValidationMessage("");
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Run Pitch Schedule</DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* New brands */}
          <Grid item xs={12}>
            <strong>How many emails do you want to send to new brands?</strong>
            {validation === "numberOfBrands" ? (
              <FormHelperText error>
                <em>{validationMessage}</em>
              </FormHelperText>
            ) : (
              <FormHelperText>
                Select a number between 0 and {MAX_BRANDS_LIMIT}.
              </FormHelperText>
            )}
            <TextField
              sx={{ width: 120, mt: 1 }}
              size="small"
              type="number"
              value={numberOfBrands}
              onChange={(e) =>
                handleFieldChange(
                  "numberOfBrands",
                  Math.floor(Number(e.target.value)),
                )
              }
              inputProps={{ min: 0, max: MAX_BRANDS_LIMIT }}
            />
          </Grid>

          {/* Repitch brands */}
          <Grid item xs={12}>
            <strong>How many re-pitching emails do you want to send?</strong>
            {validation === "numberOfRepitchBrands" ? (
              <FormHelperText error>
                <em>{validationMessage}</em>
              </FormHelperText>
            ) : (
              <FormHelperText>
                Brands you contacted before but haven’t heard back from in the
                last 30 days.
              </FormHelperText>
            )}
            <TextField
              sx={{ width: 120, mt: 1 }}
              size="small"
              type="number"
              value={numberOfRepitchBrands}
              onChange={(e) =>
                handleFieldChange(
                  "numberOfRepitchBrands",
                  Math.floor(Number(e.target.value)),
                )
              }
              inputProps={{ min: 0, max: MAX_BRANDS_LIMIT }}
            />
          </Grid>

          {/* Week selection */}
          <Grid item xs={12}>
            <Typography gutterBottom>
              <strong>When do you want to send these emails?</strong>
            </Typography>
            <Select
              size="small"
              value={weekSelection}
              onChange={(event: SelectChangeEvent<WeekSelection>) =>
                setWeekSelection(event.target.value as WeekSelection)
              }
            >
              <MenuItem value={WeekSelection.none}>
                <em>Please select</em>
              </MenuItem>
              <MenuItem value={WeekSelection.this_week}>This Week</MenuItem>
              <MenuItem value={WeekSelection.next_week}>Next Week</MenuItem>
            </Select>
            <FormHelperText>Emails are sent Monday - Thursday.</FormHelperText>
          </Grid>

          {/* Override dates */}
          <Grid item xs={12}>
            <Typography>
              <strong>Optional: Override with custom date range</strong>
            </Typography>
            <Typography variant="caption" paragraph>
              <strong>
                Week Selection must be unset for these dates to be used!
              </strong>
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Start date"
                  type="date"
                  size="small"
                  fullWidth
                  value={overrideStartDate ?? ""}
                  onChange={(e) => setOverrideStartDate(e.target.value || null)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="End date"
                  type="date"
                  size="small"
                  fullWidth
                  value={overrideEndDate ?? ""}
                  onChange={(e) => setOverrideEndDate(e.target.value || null)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <FormHelperText>
              If set, this date range overrides the week selection above.
            </FormHelperText>
          </Grid>

          {validation === "general" && (
            <Grid item xs={12}>
              <FormHelperText error>
                <em>{validationMessage}</em>
              </FormHelperText>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          disableElevation
          onClick={handleRun}
          loading={loading}
        >
          Run
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
